import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import ReactPaginate from "react-paginate";
import supabase from "../../supabaseClient";
import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/RCS-Logo-Blue.png";
import { fetchAllSupabaseRows } from "../../utils/supabasePagination";

dayjs.extend(customParseFormat);

const Analytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(dayjs().format("YYYY"));
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("MM"));
  const [selectedType, setSelectedType] = useState("ALL");
  const [trendPeriod, setTrendPeriod] = useState(12); // 3, 6, 12, or 'yearly'
  const [kpiTablePage, setKpiTablePage] = useState(0); // Pagination for KPI table
  const [kpiStatusFilter, setKpiStatusFilter] = useState(null); // Filter by KPI status
  const [kpiTypeFilter, setKpiTypeFilter] = useState(null); // Filter by shipment type (AIR/SEA/BROKERAGE)
  const [kpiTransportFilter, setKpiTransportFilter] = useState(null); // Filter by transport mode (AIR/SEA) for BROKERAGE
  const [selectedShipment, setSelectedShipment] = useState(null); // For modal
  const [shipments, setShipments] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);

  // Ref for scrolling to table
  const kpiTableRef = useRef(null);

  // Handle KPI status click
  const handleKpiStatusClick = (status, shipmentType, transportMode = null) => {
    if (kpiStatusFilter === status && kpiTypeFilter === shipmentType && kpiTransportFilter === transportMode) {
      // If clicking the same status, type, and transport mode, clear the filters
      setKpiStatusFilter(null);
      setKpiTypeFilter(null);
      setKpiTransportFilter(null);
    } else {
      // Set the new status, type, and transport mode filters
      setKpiStatusFilter(status);
      setKpiTypeFilter(shipmentType);
      setKpiTransportFilter(transportMode);
      // Reset to first page
      setKpiTablePage(0);
      // Scroll to table
      setTimeout(() => {
        kpiTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  // Modal functions
  const openModal = (shipment) => {
    setSelectedShipment(shipment);
  };

  const closeModal = () => {
    setSelectedShipment(null);
  };

  // Status color helper
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "order placed":
        return "bg-blue/10 text-blue border border-blue/30";
      case "in warehouse":
        return "bg-yellow/10 text-yellow border border-yellow/30";
      case "departure at origin":
        return "bg-purple-100 text-purple-700 border border-purple-300";
      case "in transit":
        return "bg-orange-100 text-orange-700 border border-orange-300";
      case "arrival at destination":
        return "bg-green-100 text-green-700 border border-green-300";
      case "custom clearance":
        return "bg-indigo-100 text-indigo-700 border border-indigo-300";
      case "delivered":
        return "bg-green-100 text-green-800 border border-green-300";
      case "turnover":
        return "bg-green-100 text-green-800 border border-green-300";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-300";
    }
  };

  // Shipment type color helper
  const getShipmentTypeColor = (type) => {
    switch (type) {
      case "AIR":
        return "bg-gradient-to-r from-blue to-blue/80 text-white";
      case "SEA":
        return "bg-gradient-to-r from-teal-500 to-teal-600 text-white";
      case "LAND":
        return "bg-gradient-to-r from-green-500 to-green-600 text-white";
      case "BROKERAGE":
        return "bg-gradient-to-r from-purple-500 to-purple-600 text-white";
      case "DOMESTIC":
        return "bg-gradient-to-r from-yellow to-yellow/80 text-blue";
      default:
        return "bg-gradient-to-r from-gray-500 to-gray-600 text-white";
    }
  };

  // Download PDF function
  const downloadShipmentPDF = useCallback(() => {
    if (selectedShipment) {
      const pdf = new jsPDF();

      // Add the logo
      const logoX = 10;
      const logoY = 10;
      const logoWidth = 40;
      const logoHeight = 20;
      pdf.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

      // Title
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text("Shipment Report", 105, logoY + 15, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);
      pdf.text(`BL #: ${selectedShipment.tracking_no || "—"}`, 105, logoY + 22, {
        align: "center",
      });

      let currentY = logoY + logoHeight + 32;
      const filterValidFields = (data) => data.filter(row => row.details !== null && row.details !== undefined && row.details !== "");

      // Section: Shipper Details
      pdf.setFontSize(16);
      pdf.text("Shipper Details", 10, currentY);
      currentY += 8;

      const shipperDetails = filterValidFields([
        { description: "Shipper Name", details: selectedShipment.shipper_name },
        { description: "Shipper Unit", details: selectedShipment.shipper_unit },
        { description: "Shipper Email", details: selectedShipment.shipper_email },
        { description: "Shipper City", details: selectedShipment.shipper_city },
        { description: "Shipper Street", details: selectedShipment.shipper_street },
        { description: "Shipper Country", details: selectedShipment.shipper_country },
        { description: "Shipper Phone", details: selectedShipment.shipper_phone },
        { description: "Shipper Building", details: selectedShipment.shipper_building },
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: shipperDetails.map(row => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Receiver Details
      pdf.setFontSize(16);
      pdf.text("Receiver Details", 10, currentY);
      currentY += 8;

      const receiverDetails = filterValidFields([
        { description: "Receiver Name", details: selectedShipment.receiver_name },
        { description: "Receiver Unit", details: selectedShipment.receiver_unit },
        { description: "Receiver Email", details: selectedShipment.receiver_email },
        { description: "Receiver City", details: selectedShipment.receiver_city },
        { description: "Receiver Street", details: selectedShipment.receiver_street },
        { description: "Receiver Country", details: selectedShipment.receiver_country },
        { description: "Receiver Phone", details: selectedShipment.receiver_phone },
        { description: "Receiver Building", details: selectedShipment.receiver_building },
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: receiverDetails.map(row => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Shipment Details
      pdf.setFontSize(16);
      pdf.text("Shipment Details", 10, currentY);
      currentY += 8;

      const shipmentDetails = filterValidFields([
        { description: "Shipment ID", details: selectedShipment.shipment_id },
        { description: "Job Order", details: selectedShipment.job_order },
        { description: "Type", details: selectedShipment.shipment_type },
        { description: "Origin", details: selectedShipment.shipper_city },
        { description: "Destination", details: selectedShipment.receiver_city },
        { description: "Status", details: selectedShipment.status },
        { description: "Created By", details: selectedShipment.created_by },
        { description: "Description", details: selectedShipment.shipment_description },
        { description: "Shipment Code", details: selectedShipment.shipment_code },

        // Additional fields from shipmentDetails
        { description: "Shipment Description", details: selectedShipment.shipment_description || null },
        { description: "Shipment Type", details: selectedShipment.shipment_type || null },
        { description: "Shipment Instructions", details: selectedShipment.shipment_instructions || null },
        { description: "Shipment Mode", details: selectedShipment.shipment_mode || null },
        { description: "Shipment Transport", details: selectedShipment.shipment_transport || null },

        // Sea-specific fields
        { description: "Shipment Terms", details: selectedShipment.shipment_terms || null },
        { description: "Shipment Packages", details: selectedShipment.shipment_packages || null },
        { description: "Shipment Volume", details: selectedShipment.shipment_volume || null },

        // Sea FCL-specific fields
        { description: "Shipment Containers", details: Array.isArray(selectedShipment.shipment_containers) && selectedShipment.shipment_containers.length > 0 ? selectedShipment.shipment_containers.join(", ") : null },
        { description: "Shipment Weight", details: selectedShipment.shipment_weight || null },

        // Air-specific fields
        { description: "Shipment Dimensions", details: Array.isArray(selectedShipment.shipment_dimensions) && selectedShipment.shipment_dimensions.length > 0 ? selectedShipment.shipment_dimensions.join(", ") : null },
        { description: "Shipment Chargeable Weight", details: selectedShipment.shipment_chargeableweight || null },

        // Sea LCL and Air-specific fields
        { description: "Shipment Gross Weight", details: selectedShipment.shipment_grossweight || null },
        { description: "Shipment Bound", details: selectedShipment.shipment_bound || null },
        { description: "Commodity", details: selectedShipment.commodity || null },
        { description: "Number of Pieces", details: selectedShipment.number_pcs || null },
        { description: "CBM", details: selectedShipment.cbm || null },
        { description: "Origin", details: selectedShipment.origin || null },
        { description: "Destination", details: selectedShipment.destination || null },
        { description: "Flight", details: selectedShipment.flight || null },
        { description: "Truck Type", details: selectedShipment.truck_type || null }
      ]);

      pdf.autoTable({
        startY: currentY,
        head: [["Description", "Details"]],
        body: shipmentDetails.map(row => [row.description, row.details]),
        theme: "grid",
        styles: { fontSize: 10, cellPadding: 2 },
        headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
        bodyStyles: { fillColor: [240, 240, 240] },
      });

      currentY = pdf.lastAutoTable.finalY + 10;

      // Section: Brokerage Remarks (if applicable)
      if (selectedShipment.shipment_type === "BROKERAGE" && selectedShipment.brokerage_remarks?.length > 0) {
        pdf.setFontSize(16);
        pdf.text("Brokerage Remarks", 10, currentY);
        currentY += 8;

        const sortedBrokerageRemarks = selectedShipment.brokerage_remarks.sort((a, b) =>
          new Date(b.timestamp) - new Date(a.timestamp)
        );

        const brokerageTableData = sortedBrokerageRemarks.map((remark) => ({
          timestamp: new Date(remark.timestamp).toLocaleString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            timeZone: 'Asia/Manila',
          }),
          title: remark.brokerage_title,
          input: remark.brokerage_input,
        }));

        pdf.autoTable({
          startY: currentY,
          head: [["Date and Time", "Brokerage Remarks Title", "Brokerage Remarks"]],
          body: brokerageTableData.map(row => [row.timestamp, row.title, row.input]),
          theme: "grid",
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
          bodyStyles: { fillColor: [240, 240, 240] },
        });

        currentY = pdf.lastAutoTable.finalY + 10;
      }

      // Section: Remarks (if applicable)
      if (selectedShipment.remarks && selectedShipment.remarks.length > 0) {
        pdf.setFontSize(16);
        pdf.text("Remarks", 10, currentY);
        currentY += 8;

        const sortedRemarks = selectedShipment.remarks.sort((a, b) =>
          new Date(b.date) - new Date(a.date)
        );

        const remarksTableData = sortedRemarks.map((remark) => {
          // Fallback values for missing fields
          const formatDate = (date) =>
            date
              ? new Date(date).toLocaleString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                  timeZone: "Asia/Manila",
                })
              : "Date not set";

          const receivedDate = formatDate(remark.receivedDate);
          const transitDate = formatDate(remark.transitDate);
          const aad = formatDate(remark.aad);
          const atd = formatDate(remark.atd);
          const eta = formatDate(selectedShipment.eta);
          const etd = formatDate(selectedShipment.etd);

          // Determine shipment status
          const orderPlaced = remark.status === "Order Placed";
          const departureAtOrigin = remark.status === "Departure at Origin";
          const arrivalAtDestination = remark.status === "Arrival at Destination";
          const inTransit = remark.status === "In Transit";

          // Construct remarks string dynamically
          let fullRemark = remark.remark ? `${remark.remark}\n` : "No Remarks Available\n";

          if (orderPlaced) fullRemark += `Shipment Nomination Date: ${receivedDate}\n`;
          if (orderPlaced || departureAtOrigin) fullRemark += `ETD: ${etd}\n`;
          if (departureAtOrigin) fullRemark += `ATD: ${atd}\n`;
          if (orderPlaced || arrivalAtDestination) fullRemark += `ETA: ${eta}\n`;
          if (arrivalAtDestination) fullRemark += `ATA: ${aad}\n`;

          // Add Transit Type & Transit Date for "In Transit" status
          if (inTransit) {
            fullRemark += `Transit Type: ${remark.transitType || "Date not set"}\n`;
            fullRemark += `Transit Date: ${remark.transitDate || "Date not set"}\n`;
            fullRemark += `ETD: ${etd}\n`;
            fullRemark += `ETA: ${eta}\n`;
          }

          return {
            dateTime: formatDate(remark.date),
            status: remark.status || "No Status Available",
            remarks: fullRemark.trim(),
          };
        });

        // Generate remarks table
        pdf.autoTable({
          startY: currentY,
          head: [["Date and Time", "Status", "Remarks"]],
          body: remarksTableData.map((row) => [row.dateTime, row.status, row.remarks]),
          theme: "grid",
          styles: { fontSize: 10, cellPadding: 3 },
          headStyles: { fontStyle: "bold", fillColor: [17, 72, 124] },
          bodyStyles: { fillColor: [240, 240, 240], cellWidth: "wrap" },
        });
      }

      // Save the PDF
      pdf.save(`shipment-${selectedShipment.tracking_no}.pdf`);
    } else {
      console.error("No shipment selected for download.");
    }
  }, [selectedShipment]);

  // Color palette matching your design
  const COLORS = {
    blue: "#114878",
    yellow: "#FDB913",
    grey: "#333333",
    green: "#10b981",
    orange: "#f97316",
    purple: "#8b5cf6",
    pink: "#ec4899",
  };

  // Get icon for shipment type
  const getShipmentTypeIcon = (type) => {
    const typeUpper = type?.toUpperCase();
    switch (typeUpper) {
      case "AIR":
        return "✈️";
      case "SEA":
        return "🚢";
      case "LAND":
        return "🚛";
      case "BROKERAGE":
        return "📋";
      case "DOMESTIC":
        return "🏠";
      default:
        return "📦";
    }
  };

  // Calculate KPI status for shipments
  const getKPIStatus = (shipment) => {
    const shipmentType = shipment.shipment_type?.toUpperCase();

    // Only calculate for AIR, SEA, and BROKERAGE shipments
    if (shipmentType !== "AIR" && shipmentType !== "SEA" && shipmentType !== "BROKERAGE") {
      return null;
    }

    // Find the "Arrival at Destination" remark to get the aad (Actual Arrival Date)
    const arrivalRemark = shipment.remarks?.find(
      (r) => r.status === "Arrival at Destination"
    );

    if (!arrivalRemark) {
      console.log(`[NO ARRIVAL REMARK] ${shipment.tracking_no} - No "Arrival at Destination" remark found`);
      return null; // No arrival remark found
    }

    // Only use aad field - if missing, it's "No Arrival Data"
    if (!arrivalRemark.aad || arrivalRemark.aad === "" || arrivalRemark.aad === null) {
      console.log(`[NO AAD] ${shipment.tracking_no} - aad is null/empty:`, arrivalRemark.aad);
      return null; // No arrival date recorded yet
    }

    console.log(`[HAS AAD] ${shipment.tracking_no} - Found aad:`, arrivalRemark.aad);

    // Parse arrival date - handle various datetime formats
    let arrivalDate = dayjs(arrivalRemark.aad);

    // Log all parsing attempts for debugging
    console.log(`[PARSING] ${shipment.tracking_no} - aad:`, arrivalRemark.aad, "- isValid:", arrivalDate.isValid());

    // If standard parsing fails, try custom formats
    if (!arrivalDate.isValid()) {
      console.log(`[PARSE FAIL] ${shipment.tracking_no} - Standard parsing failed for aad:`, arrivalRemark.aad);

      arrivalDate = dayjs(arrivalRemark.aad, [
        "YYYY-MM-DDTHH:mm:ss",
        "YYYY-MM-DDTHH:mm",
        "YYYY-MM-DDTHH:mm:ss.SSSZ",
        "YYYY-MM-DD HH:mm:ss",
        "YYYY-MM-DD HH:mm",
      ]);

      if (arrivalDate.isValid()) {
        console.log(`[PARSE SUCCESS] ${shipment.tracking_no} - Custom format parsing succeeded`);
      }
    }

    // Check if date is valid after all parsing attempts
    if (!arrivalDate.isValid()) {
      console.log(`[INVALID] Shipment ${shipment.tracking_no} has invalid aad format:`, arrivalRemark.aad);
      return null; // Invalid arrival date
    }

    // Find delivery/turnover date
    let completionDate;
    if (shipment.status === "Delivered" || shipment.status === "Turnover") {
      // Find the remark with Delivered or Turnover status
      const completionRemark = shipment.remarks?.find(
        (r) => r.status === "Delivered" || r.status === "Turnover"
      );

      if (completionRemark && completionRemark.date) {
        completionDate = dayjs(completionRemark.date);
      } else {
        // Fallback to current date if shipment is completed but no completion date
        completionDate = dayjs();
      }
    } else {
      // If not yet completed, use current date
      completionDate = dayjs();
    }

    // Calculate days between arrival and completion/current
    const daysDiff = completionDate.diff(arrivalDate, "day");

    // If arrival date is in the future, treat as "On Time" with 0 days
    if (daysDiff < 0) {
      console.log(`[FUTURE ARRIVAL] ${shipment.tracking_no} - Arrival date is in the future, treating as On Time`);
      return {
        status: "On Time",
        color: "green",
        bgColor: "bg-green-100",
        textColor: "text-green-700",
        borderColor: "border-green-300",
        days: 0,
      };
    }

    // AIR shipment KPI (1-2 days is On Time, >2 days is Delayed)
    if (shipmentType === "AIR") {
      if (daysDiff >= 0 && daysDiff <= 2) {
        return {
          status: "On Time",
          color: "green",
          bgColor: "bg-green-100",
          textColor: "text-green-700",
          borderColor: "border-green-300",
          days: daysDiff,
        };
      } else if (daysDiff > 2) {
        return {
          status: "Delayed",
          color: "red",
          bgColor: "bg-red-100",
          textColor: "text-red-700",
          borderColor: "border-red-300",
          days: daysDiff,
        };
      }
    }

    // SEA shipment KPI (1-5 days On Time, 6-10 Slightly Delayed, >10 Delayed)
    if (shipmentType === "SEA") {
      if (daysDiff >= 0 && daysDiff <= 5) {
        return {
          status: "On Time",
          color: "green",
          bgColor: "bg-green-100",
          textColor: "text-green-700",
          borderColor: "border-green-300",
          days: daysDiff,
        };
      } else if (daysDiff >= 6 && daysDiff <= 10) {
        return {
          status: "Slightly Delayed",
          color: "orange",
          bgColor: "bg-orange-200",
          textColor: "text-orange-700",
          borderColor: "border-orange-300",
          days: daysDiff,
        };
      } else if (daysDiff > 10) {
        return {
          status: "Delayed",
          color: "red",
          bgColor: "bg-red-100",
          textColor: "text-red-700",
          borderColor: "border-red-300",
          days: daysDiff,
        };
      }
    }

    // BROKERAGE shipment KPI - Based on shipment_transport (AIR or SEA)
    if (shipmentType === "BROKERAGE") {
      const transportMode = shipment.shipment_transport;

      if (transportMode === "AIR") {
        // Use AIR KPI logic (1-2 days)
        if (daysDiff >= 0 && daysDiff <= 2) {
          return {
            status: "On Time",
            color: "green",
            bgColor: "bg-green-100",
            textColor: "text-green-700",
            borderColor: "border-green-300",
            days: daysDiff,
          };
        } else if (daysDiff > 2) {
          return {
            status: "Delayed",
            color: "red",
            bgColor: "bg-red-100",
            textColor: "text-red-700",
            borderColor: "border-red-300",
            days: daysDiff,
          };
        }
      } else if (transportMode === "SEA") {
        // Use SEA KPI logic (1-5 days On Time, 6-10 Slightly Delayed, >10 Delayed)
        if (daysDiff >= 0 && daysDiff <= 5) {
          return {
            status: "On Time",
            color: "green",
            bgColor: "bg-green-100",
            textColor: "text-green-700",
            borderColor: "border-green-300",
            days: daysDiff,
          };
        } else if (daysDiff >= 6 && daysDiff <= 10) {
          return {
            status: "Slightly Delayed",
            color: "orange",
            bgColor: "bg-orange-200",
            textColor: "text-orange-700",
            borderColor: "border-orange-300",
            days: daysDiff,
          };
        } else if (daysDiff > 10) {
          return {
            status: "Delayed",
            color: "red",
            bgColor: "bg-red-100",
            textColor: "text-red-700",
            borderColor: "border-red-300",
            days: daysDiff,
          };
        }
      }
    }

    return null;
  };

  // Fetch all shipments data
  useEffect(() => {
    const fetchShipments = async () => {
      setLoading(true);
      try {
        const { data, error } = await fetchAllSupabaseRows(() =>
          supabase.from("shipments").select("*").order("date", { ascending: true })
        );

        if (error) throw error;
        setShipments(data || []);
      } catch (error) {
        console.error("Error fetching shipments:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();

    // Add real-time subscription
    const subscription = supabase
      .channel("analytics-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "shipments" },
        async () => {
          const { data, error } = await fetchAllSupabaseRows(() =>
            supabase.from("shipments").select("*").order("date", { ascending: true })
          );
          if (!error && data) {
            setShipments(data);
          }
        }
      )
      .subscribe();

    return () => {
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, []);

  // Fixed list of transport types for filter
  const shipmentTypes = ["ALL", "AIR", "BROKERAGE", "DOMESTIC", "LAND", "SEA"];

  // Helper function to extract month and year from Job Order
  const extractDateFromJobOrder = (jobOrder) => {
    if (!jobOrder || jobOrder.length < 8) return null;

    try {
      // Format: [PREFIX][MONTH][COUNT][YEAR]
      // Example: BS020826 → Month 02, Year 26
      const month = parseInt(jobOrder.substring(2, 4)); // Position 2-3
      const year = parseInt(jobOrder.substring(6, 8));  // Position 6-7

      // Validate month (1-12)
      if (month < 1 || month > 12) return null;

      // Convert 2-digit year to 4-digit (26 → 2026, 25 → 2025)
      const fullYear = 2000 + year;

      return { month, year: fullYear };
    } catch (error) {
      return null;
    }
  };

  // Calculate analytics data
  const analytics = useMemo(() => {
    if (!shipments.length) return null;

    // Filter by year and month with "All" support (using Job Order only)
    let selectedMonthShipments = shipments.filter((row) => {
      const joDate = extractDateFromJobOrder(row.job_order);
      if (!joDate) return false;

      // Match month filter - if "All", skip month check
      const matchesMonth =
        selectedMonth === "All"
          ? true
          : joDate.month === parseInt(selectedMonth);

      // Match year filter - if "All", skip year check
      const matchesYear =
        selectedYear === "All"
          ? true
          : joDate.year === parseInt(selectedYear);

      return matchesMonth && matchesYear;
    });

    // Apply shipment type filter (SEA, AIR, LAND, BROKERAGE, DOMESTIC)
    // Matching SuperShipmentManagement logic exactly - no trim(), no toUpperCase()
    if (selectedType !== "ALL") {
      selectedMonthShipments = selectedMonthShipments.filter(
        (row) => row.shipment_type === selectedType
      );
    }

    // Total job orders for selected month
    const totalJobOrders = selectedMonthShipments.length;

    // Breakdown by shipment type - using shipment_type field
    const modeBreakdown = selectedMonthShipments.reduce((acc, shipment) => {
      let type = shipment.shipment_type?.trim();

      // Handle empty/null/undefined values
      if (!type || type === "" || type === "null" || type === "undefined") {
        type = "Not Specified";
      }

      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const modeData = Object.entries(modeBreakdown)
      .map(([name, value]) => ({
        name,
        value,
        percentage: ((value / totalJobOrders) * 100).toFixed(1),
      }))
      .sort((a, b) => b.value - a.value); // Sort by count descending

    // Month-over-month comparison (3, 6, or 12 months from selected date)
    let trendData = [];

    if (trendPeriod === "yearly") {
      // Yearly trend from earliest data year (using Job Order)
      const yearsWithData = shipments
        .map((s) => {
          const joDate = extractDateFromJobOrder(s.job_order);
          if (joDate) return joDate.year;
          if (s.date) return new Date(s.date).getFullYear();
          return null;
        })
        .filter((year) => year !== null);

      if (yearsWithData.length > 0) {
        const earliestYear = Math.min(...yearsWithData);
        const currentYear = dayjs().year();

        for (let year = earliestYear; year <= currentYear; year++) {
          const count = shipments.filter((s) => {
            const joDate = extractDateFromJobOrder(s.job_order);
            if (joDate) return joDate.year === year;
            if (s.date) return new Date(s.date).getFullYear() === year;
            return false;
          }).length;

          trendData.push({
            month: year.toString(),
            count,
            fullDate: year.toString(),
          });
        }
      }
    } else {
      // Monthly trend (3, 6, or 12 months) - using Job Order
      const selectedDate = dayjs(`${selectedYear}-${selectedMonth}-01`);
      const monthsToShow = trendPeriod - 1;

      for (let i = monthsToShow; i >= 0; i--) {
        const targetDate = selectedDate.subtract(i, "month");
        const targetMonth = targetDate.month() + 1; // 1-12
        const targetYear = targetDate.year();
        const monthName = targetDate.format("MMM YYYY");

        const count = shipments.filter((s) => {
          const joDate = extractDateFromJobOrder(s.job_order);
          if (joDate) {
            return joDate.month === targetMonth && joDate.year === targetYear;
          }
          // Fallback to date
          if (s.date) {
            return dayjs(s.date).format("YYYY-MM") === targetDate.format("YYYY-MM");
          }
          return false;
        }).length;

        trendData.push({
          month: monthName,
          count,
          fullDate: targetDate.format("YYYY-MM"),
        });
      }
    }

    const monthlyComparison = trendData;

    // Calculate KPI metrics for AIR and SEA shipments
    const airShipments = selectedMonthShipments.filter(
      (s) => s.shipment_type === "AIR"
    );
    const seaShipments = selectedMonthShipments.filter(
      (s) => s.shipment_type === "SEA"
    );

    const airKPI = {
      total: airShipments.length,
      onTime: 0,
      delayed: 0,
      noArrivalData: 0,
    };

    const seaKPI = {
      total: seaShipments.length,
      onTime: 0,
      slightlyDelayed: 0,
      delayed: 0,
      noArrivalData: 0,
    };

    // Filter BROKERAGE shipments and separate by transport mode
    const brokerageShipments = selectedMonthShipments.filter(
      (s) => s.shipment_type === "BROKERAGE"
    );

    // Debug: Check BROKERAGE counts
    console.log("=== BROKERAGE DEBUG ===");
    console.log("Total shipments from DB:", shipments.length);
    console.log("Total BROKERAGE (before date filter):", shipments.filter(s => s.shipment_type === "BROKERAGE").length);
    console.log("Total BROKERAGE (after date filter):", brokerageShipments.length);
    console.log("BROKERAGE without date:", shipments.filter(s => s.shipment_type === "BROKERAGE" && !s.date).length);
    console.log("BROKERAGE with SEA transport (before date filter):", shipments.filter(s => s.shipment_type === "BROKERAGE" && s.shipment_transport === "SEA").length);

    const brokerageAirShipments = brokerageShipments.filter(
      (s) => s.shipment_transport === "AIR"
    );
    const brokerageSeaShipments = brokerageShipments.filter(
      (s) => s.shipment_transport === "SEA"
    );
    const brokerageNoTransport = brokerageShipments.filter(
      (s) => !s.shipment_transport || (s.shipment_transport !== "AIR" && s.shipment_transport !== "SEA")
    );

    console.log("BROKERAGE AIR:", brokerageAirShipments.length);
    console.log("BROKERAGE SEA:", brokerageSeaShipments.length);
    console.log("BROKERAGE No/Other Transport:", brokerageNoTransport.length);
    console.log("Sum (AIR + SEA):", brokerageAirShipments.length + brokerageSeaShipments.length);
    if (brokerageNoTransport.length > 0) {
      console.log("Sample BROKERAGE without transport:", brokerageNoTransport.slice(0, 3).map(s => ({
        tracking: s.tracking_no,
        transport: s.shipment_transport
      })));
    }

    const brokerageAirKPI = {
      total: brokerageAirShipments.length,
      onTime: 0,
      delayed: 0,
      noArrivalData: 0,
    };

    const brokerageSeaKPI = {
      total: brokerageSeaShipments.length,
      onTime: 0,
      slightlyDelayed: 0,
      delayed: 0,
      noArrivalData: 0,
    };

    // Calculate AIR KPI
    console.log(`[AIR] Processing ${airShipments.length} shipments`);
    airShipments.forEach((shipment) => {
      const kpiStatus = getKPIStatus(shipment);
      if (!kpiStatus) {
        console.log(`[AIR] ${shipment.tracking_no} - No KPI status (No Arrival Data)`);
        airKPI.noArrivalData++;
        return;
      }

      console.log(`[AIR] ${shipment.tracking_no} - KPI: ${kpiStatus.status}`);
      if (kpiStatus.status === "On Time") {
        airKPI.onTime++;
      } else if (kpiStatus.status === "Delayed") {
        airKPI.delayed++;
      }
    });

    // Calculate SEA KPI
    console.log(`[SEA] Processing ${seaShipments.length} shipments`);
    seaShipments.forEach((shipment) => {
      const kpiStatus = getKPIStatus(shipment);
      if (!kpiStatus) {
        console.log(`[SEA] ${shipment.tracking_no} - No KPI status (No Arrival Data)`);
        seaKPI.noArrivalData++;
        return;
      }

      console.log(`[SEA] ${shipment.tracking_no} - KPI: ${kpiStatus.status}`);
      if (kpiStatus.status === "On Time") {
        seaKPI.onTime++;
      } else if (kpiStatus.status === "Slightly Delayed") {
        seaKPI.slightlyDelayed++;
      } else if (kpiStatus.status === "Delayed") {
        seaKPI.delayed++;
      }
    });

    // Calculate BROKERAGE AIR KPI
    brokerageAirShipments.forEach((shipment) => {
      const kpiStatus = getKPIStatus(shipment);
      if (!kpiStatus) {
        brokerageAirKPI.noArrivalData++;
        return;
      }

      if (kpiStatus.status === "On Time") {
        brokerageAirKPI.onTime++;
      } else if (kpiStatus.status === "Delayed") {
        brokerageAirKPI.delayed++;
      }
    });

    // Calculate BROKERAGE SEA KPI
    console.log(`[BROKERAGE SEA] Processing ${brokerageSeaShipments.length} shipments`);
    brokerageSeaShipments.forEach((shipment) => {
      const kpiStatus = getKPIStatus(shipment);
      if (!kpiStatus) {
        console.log(`[BROKERAGE SEA] ${shipment.tracking_no} - No KPI status (No Arrival Data)`);
        brokerageSeaKPI.noArrivalData++;
        return;
      }

      console.log(`[BROKERAGE SEA] ${shipment.tracking_no} - KPI: ${kpiStatus.status}`);
      if (kpiStatus.status === "On Time") {
        brokerageSeaKPI.onTime++;
      } else if (kpiStatus.status === "Slightly Delayed") {
        brokerageSeaKPI.slightlyDelayed++;
      } else if (kpiStatus.status === "Delayed") {
        brokerageSeaKPI.delayed++;
      }
    });

    // Helper function to check if shipment is completed
    const isShipmentCompleted = (shipment) => {
      // Standard completed statuses
      if (["delivered", "turnover"].includes(shipment.status?.toLowerCase())) {
        return true;
      }

      // For AIR EXPORT shipments with DOOR-PORT or PORT-PORT delivery mode,
      // "Arrival at Destination" is considered completed
      const isAirExportDoorOrPortToPort =
        shipment.shipment_type === "AIR" &&
        (shipment.shipment_mode === "EXPORT" || shipment.shipment_mode === "EXPORT-EVENT") &&
        (shipment.delivery_mode === "DOOR-PORT" || shipment.delivery_mode === "PORT-PORT");

      if (isAirExportDoorOrPortToPort && shipment.status === "Arrival at Destination") {
        return true;
      }

      return false;
    };

    // Calculate counts for metric cards (matching CompletedShipment.jsx logic)
    // "This Month" = Completed shipments (status: "Delivered" OR "Turnover") for selected month
    // CompletedShipment.jsx line 874: ["turnover", "delivered"].includes(shipment.status.toLowerCase())
    const currentMonthCount = selectedMonthShipments.filter((s) =>
      isShipmentCompleted(s)
    ).length;

    // "Last Month" = Completed shipments (status: "Delivered" OR "Turnover") for previous month
    const previousMonthDate = dayjs(
      `${selectedYear}-${selectedMonth}-01`
    ).subtract(1, "month");
    const previousMonthShipments = shipments.filter((row) => {
      const joDate = extractDateFromJobOrder(row.job_order);
      if (!joDate) return false;

      // Use Job Order date
      const matchesMonth = joDate.month === previousMonthDate.month() + 1;
      const matchesYear = joDate.year === previousMonthDate.year();
      return matchesMonth && matchesYear;
    });

    const previousMonthCount = previousMonthShipments.filter((s) =>
      isShipmentCompleted(s)
    ).length;

    // Calculate growth percentage
    const growthPercentage =
      previousMonthCount > 0
        ? (
            ((currentMonthCount - previousMonthCount) / previousMonthCount) *
            100
          ).toFixed(1)
        : 0;

    return {
      totalJobOrders,
      modeData,
      monthlyComparison,
      growthPercentage,
      currentMonthCount,
      previousMonthCount,
      airKPI,
      seaKPI,
      brokerageAirKPI,
      brokerageSeaKPI,
    };
  }, [shipments, selectedYear, selectedMonth, selectedType, trendPeriod]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-blue/20">
          <p className="text-blue font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-grey text-sm">
              {entry.name}:{" "}
              <span className="font-bold text-blue">{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <section className="flex flex-col justify-center font-poppins min-h-screen bg-gray-100">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto">
              <div className="w-16 h-16 border-4 border-blue/20 border-t-blue rounded-full animate-spin"></div>
            </div>
            <div className="text-blue font-medium text-lg mt-4">
              Loading Analytics...
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col justify-center font-poppins min-h-screen bg-gray-100">
      <div className="flex flex-col p-6 gap-6">
        {/* Header */}
        <div className="text-center mb-2">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-12 h-12 bg-blue rounded-2xl flex items-center justify-center shadow-lg">
              <i className="fas fa-chart-line text-white text-xl"></i>
            </div>
            <h1 className="text-4xl font-bold text-blue">
              Analytics & Reporting
            </h1>
          </div>
          <p className="text-grey font-medium">
            Comprehensive insights and performance metrics
          </p>
        </div>

        {/* Active Filters Indicator */}
        <div className="flex flex-wrap items-center gap-3">
          {selectedYear !== "All" && (
            <div className="bg-blue/10 border-2 border-blue/20 rounded-full px-4 py-2 flex items-center gap-2">
              <i className="fas fa-calendar-alt text-blue"></i>
              <span className="text-blue font-semibold text-sm">
                Year: {selectedYear}
              </span>
            </div>
          )}
          {selectedMonth !== "All" && (
            <div className="bg-blue/10 border-2 border-blue/20 rounded-full px-4 py-2 flex items-center gap-2">
              <i className="fas fa-calendar text-blue"></i>
              <span className="text-blue font-semibold text-sm">
                {dayjs(`2024-${selectedMonth}-01`).format("MMMM")}
              </span>
            </div>
          )}
          {selectedType !== "ALL" && (
            <div className="bg-blue/10 border-2 border-blue/20 rounded-full px-4 py-2 flex items-center gap-2">
              <span className="text-xl">
                {getShipmentTypeIcon(selectedType)}
              </span>
              <span className="text-blue font-semibold text-sm">
                {selectedType}
              </span>
            </div>
          )}
          {selectedYear === "All" &&
            selectedMonth === "All" &&
            selectedType === "ALL" && (
              <div className="bg-green-100 border-2 border-green-300 rounded-full px-4 py-2 flex items-center gap-2">
                <i className="fas fa-globe text-green-600"></i>
                <span className="text-green-700 font-semibold text-sm">
                  Viewing All Data
                </span>
              </div>
            )}
        </div>

        {/* Year, Month, and Type Selector */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <i className="fas fa-filter text-blue text-2xl"></i>
              <h2 className="text-2xl font-bold text-blue">Filters</h2>
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-grey">
                      Year
                    </label>
                    <select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      className="px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md text-blue font-semibold"
                    >
                      <option value="All">All Years</option>
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = dayjs().year() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-grey">
                      Month
                    </label>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      className="px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md text-blue font-semibold"
                    >
                      <option value="All">All Months</option>
                      {Array.from({ length: 12 }, (_, i) => {
                        const month = String(i + 1).padStart(2, "0");
                        const monthName = dayjs(`2024-${month}-01`).format(
                          "MMMM"
                        );
                        return (
                          <option key={month} value={month}>
                            {monthName}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-semibold text-grey">
                      Shipment Type
                    </label>
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-blue focus:ring-4 focus:ring-blue/10 transition-all duration-300 bg-white hover:border-gray-300 hover:shadow-md text-blue font-semibold min-w-[150px]"
                    >
                      {shipmentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedYear(dayjs().format("YYYY"));
                    setSelectedMonth(dayjs().format("MM"));
                    setSelectedType("ALL");
                    setKpiStatusFilter(null);
                    setKpiTypeFilter(null);
                  }}
                  className="px-6 py-3 bg-blue hover:bg-blue/80 text-white font-medium rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  <i className="fas fa-sync-alt mr-2"></i>
                  Reset Filters
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total Job Orders - Clickable */}
          <div
            onClick={() => {
              // Navigate to shipment management with filters applied
              navigate("/superAdmin/shipment-management", {
                state: {
                  month: selectedMonth,
                  year: selectedYear,
                  type: selectedType !== "ALL" ? selectedType : "",
                },
              });
            }}
            className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] cursor-pointer"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-blue/10 rounded-2xl flex items-center justify-center">
                <i className="fas fa-file-alt text-blue text-2xl"></i>
              </div>
              <h3 className="text-grey font-semibold text-lg">
                Total Job Orders
              </h3>
            </div>
            <p className="text-4xl font-bold text-blue mb-2">
              {analytics?.totalJobOrders || 0}
            </p>
            <p className="text-sm text-grey">
              {selectedYear === "All" && selectedMonth === "All"
                ? "All Time"
                : selectedYear === "All"
                ? dayjs(`2024-${selectedMonth}-01`).format("MMMM (All Years)")
                : selectedMonth === "All"
                ? `All Months ${selectedYear}`
                : dayjs(`${selectedYear}-${selectedMonth}-01`).format(
                    "MMMM YYYY"
                  )}
            </p>
            <p className="text-xs text-blue/60 mt-2">
              <i className="fas fa-arrow-right mr-1"></i>
              Click to view shipments
            </p>
          </div>

          {/* Growth Rate */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-green-100 rounded-2xl flex items-center justify-center">
                <i className="fas fa-chart-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-grey font-semibold text-lg">Growth Rate</h3>
            </div>
            <p
              className={`text-4xl font-bold mb-2 ${
                parseFloat(analytics?.growthPercentage) >= 0
                  ? "text-green-600"
                  : "text-red-500"
              }`}
            >
              {analytics?.growthPercentage >= 0 ? "+" : ""}
              {analytics?.growthPercentage || 0}%
            </p>
            <p className="text-sm text-grey">
              {selectedYear === "All" && selectedMonth === "All"
                ? "vs Previous Period"
                : "vs Previous Month"}
            </p>
          </div>

          {/* Current Month Completed */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-yellow/10 rounded-2xl flex items-center justify-center">
                <i className="fas fa-calendar-day text-yellow text-2xl"></i>
              </div>
              <h3 className="text-grey font-semibold text-lg">
                {selectedYear === "All" && selectedMonth === "All"
                  ? "All Time"
                  : "This Month"}
              </h3>
            </div>
            <p className="text-4xl font-bold text-blue mb-2">
              {analytics?.currentMonthCount || 0}
            </p>
            <p className="text-sm text-grey">Completed Shipments</p>
          </div>

          {/* Previous Month Completed */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6 hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center">
                <i className="fas fa-history text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-grey font-semibold text-lg">
                {selectedYear === "All" && selectedMonth === "All"
                  ? "Previous Period"
                  : "Last Month"}
              </h3>
            </div>
            <p className="text-4xl font-bold text-blue mb-2">
              {analytics?.previousMonthCount || 0}
            </p>
            <p className="text-sm text-grey">Completed Shipments</p>
          </div>
        </div>

        {/* KPI Indicators for Air and Sea Shipments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Air Shipment KPI */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue/10 rounded-2xl flex items-center justify-center">
                <span className="text-3xl">✈️</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-blue">
                  Air Shipments KPI
                </h3>
                <p className="text-sm text-grey">
                  Total: {analytics?.airKPI.total || 0} shipments
                </p>
                <p className="text-xs text-grey/70 mt-1">
                  Days from Arrival to Delivery/Turnover
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* On Time */}
              <div
                onClick={() => handleKpiStatusClick("On Time", "AIR")}
                className={`bg-green-50 border-2 rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                  kpiStatusFilter === "On Time" && kpiTypeFilter === "AIR"
                    ? "border-green-500 ring-4 ring-green-200"
                    : "border-green-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="font-semibold text-grey">
                      On Time (1-2 days)
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-green-700">
                    {analytics?.airKPI.onTime || 0}
                  </span>
                </div>
                <div className="w-full bg-green-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        analytics?.airKPI.total > 0
                          ? (analytics.airKPI.onTime / analytics.airKPI.total) *
                            100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                <p className="text-xs text-green-700 mt-1">
                  {analytics?.airKPI.total > 0
                    ? (
                        (analytics.airKPI.onTime / analytics.airKPI.total) *
                        100
                      ).toFixed(1)
                    : 0}
                  % of total
                </p>
              </div>

              {/* Delayed */}
              <div
                onClick={() => handleKpiStatusClick("Delayed", "AIR")}
                className={`bg-red-50 border-2 rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                  kpiStatusFilter === "Delayed" && kpiTypeFilter === "AIR"
                    ? "border-red-500 ring-4 ring-red-200"
                    : "border-red-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="font-semibold text-grey">
                      Delayed ({">"} 2 days)
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-red-700">
                    {analytics?.airKPI.delayed || 0}
                  </span>
                </div>
                <div className="w-full bg-red-200 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        analytics?.airKPI.total > 0
                          ? (analytics.airKPI.delayed /
                              analytics.airKPI.total) *
                            100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                <p className="text-xs text-red-700 mt-1">
                  {analytics?.airKPI.total > 0
                    ? (
                        (analytics.airKPI.delayed / analytics.airKPI.total) *
                        100
                      ).toFixed(1)
                    : 0}
                  % of total
                </p>
              </div>

              {/* No Arrival Data - AIR */}
              <div
                onClick={() => handleKpiStatusClick("No Arrival Data", "AIR")}
                className={`bg-gray-50 border-2 rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                  kpiStatusFilter === "No Arrival Data" && kpiTypeFilter === "AIR"
                    ? "border-gray-500 ring-4 ring-gray-200"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="font-semibold text-grey">
                      No Arrival Data
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-gray-700">
                    {analytics?.airKPI.noArrivalData || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gray-500 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        analytics?.airKPI.total > 0
                          ? (analytics.airKPI.noArrivalData / analytics.airKPI.total) * 100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                <p className="text-xs text-gray-700 mt-1">
                  {analytics?.airKPI.total > 0
                    ? ((analytics.airKPI.noArrivalData / analytics.airKPI.total) * 100).toFixed(1)
                    : 0}
                  % of total
                </p>
              </div>
            </div>
          </div>

          {/* Sea Shipment KPI */}
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue/10 rounded-2xl flex items-center justify-center">
                <span className="text-3xl">🚢</span>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-blue">
                  Sea Shipments KPI
                </h3>
                <p className="text-sm text-grey">
                  Total: {analytics?.seaKPI.total || 0} shipments
                </p>
                <p className="text-xs text-grey/70 mt-1">
                  Days from Arrival to Delivery/Turnover
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* On Time */}
              <div
                onClick={() => handleKpiStatusClick("On Time", "SEA")}
                className={`bg-green-50 border-2 rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                  kpiStatusFilter === "On Time" && kpiTypeFilter === "SEA"
                    ? "border-green-500 ring-4 ring-green-200"
                    : "border-green-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="font-semibold text-grey">
                      On Time (1-5 days)
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-green-700">
                    {analytics?.seaKPI.onTime || 0}
                  </span>
                </div>
                <div className="w-full bg-green-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        analytics?.seaKPI.total > 0
                          ? (analytics.seaKPI.onTime / analytics.seaKPI.total) *
                            100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                <p className="text-xs text-green-700 mt-1">
                  {analytics?.seaKPI.total > 0
                    ? (
                        (analytics.seaKPI.onTime / analytics.seaKPI.total) *
                        100
                      ).toFixed(1)
                    : 0}
                  % of total
                </p>
              </div>

              {/* Slightly Delayed */}
              <div
                onClick={() => handleKpiStatusClick("Slightly Delayed", "SEA")}
                className={`bg-orange-50 border-2 rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                  kpiStatusFilter === "Slightly Delayed" && kpiTypeFilter === "SEA"
                    ? "border-orange-500 ring-4 ring-orange-200"
                    : "border-orange-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <span className="font-semibold text-grey">
                      Slightly Delayed (6-10 days)
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-orange-700">
                    {analytics?.seaKPI.slightlyDelayed || 0}
                  </span>
                </div>
                <div className="w-full bg-orange-200 rounded-full h-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        analytics?.seaKPI.total > 0
                          ? (analytics.seaKPI.slightlyDelayed /
                              analytics.seaKPI.total) *
                            100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                <p className="text-xs text-orange-700 mt-1">
                  {analytics?.seaKPI.total > 0
                    ? (
                        (analytics.seaKPI.slightlyDelayed /
                          analytics.seaKPI.total) *
                        100
                      ).toFixed(1)
                    : 0}
                  % of total
                </p>
              </div>

              {/* Delayed */}
              <div
                onClick={() => handleKpiStatusClick("Delayed", "SEA")}
                className={`bg-red-50 border-2 rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                  kpiStatusFilter === "Delayed" && kpiTypeFilter === "SEA"
                    ? "border-red-500 ring-4 ring-red-200"
                    : "border-red-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="font-semibold text-grey">
                      Delayed ({">"} 10 days)
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-red-700">
                    {analytics?.seaKPI.delayed || 0}
                  </span>
                </div>
                <div className="w-full bg-red-200 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        analytics?.seaKPI.total > 0
                          ? (analytics.seaKPI.delayed /
                              analytics.seaKPI.total) *
                            100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                <p className="text-xs text-red-700 mt-1">
                  {analytics?.seaKPI.total > 0
                    ? (
                        (analytics.seaKPI.delayed / analytics.seaKPI.total) *
                        100
                      ).toFixed(1)
                    : 0}
                  % of total
                </p>
              </div>

              {/* No Arrival Data - SEA */}
              <div
                onClick={() => handleKpiStatusClick("No Arrival Data", "SEA")}
                className={`bg-gray-50 border-2 rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                  kpiStatusFilter === "No Arrival Data" && kpiTypeFilter === "SEA"
                    ? "border-gray-500 ring-4 ring-gray-200"
                    : "border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="font-semibold text-grey">
                      No Arrival Data
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-gray-700">
                    {analytics?.seaKPI.noArrivalData || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gray-500 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${
                        analytics?.seaKPI.total > 0
                          ? (analytics.seaKPI.noArrivalData / analytics.seaKPI.total) * 100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
                <p className="text-xs text-gray-700 mt-1">
                  {analytics?.seaKPI.total > 0
                    ? ((analytics.seaKPI.noArrivalData / analytics.seaKPI.total) * 100).toFixed(1)
                    : 0}
                  % of total
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* BROKERAGE Shipments KPI - Full Width */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-purple/10 rounded-2xl flex items-center justify-center">
              <span className="text-3xl">📦</span>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-blue">
                Brokerage Shipments KPI
              </h3>
              <p className="text-sm text-grey">
                Total: {(analytics?.brokerageAirKPI.total || 0) + (analytics?.brokerageSeaKPI.total || 0)} shipments
              </p>
              <p className="text-xs text-grey/70 mt-1">
                Separated by Transport Mode
              </p>
            </div>
          </div>

          {/* Two sections: AIR and SEA Transport Modes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* AIR Transport Mode Section */}
            <div className="border-2 border-blue-100 rounded-2xl p-4 bg-blue-50/30">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">✈️</span>
                <div>
                  <h4 className="text-lg font-bold text-blue">Air Transport Mode</h4>
                  <p className="text-xs text-grey">Total: {analytics?.brokerageAirKPI.total || 0} shipments</p>
                </div>
              </div>

              <div className="space-y-3">
                {/* On Time - AIR */}
                <div
                  onClick={() => handleKpiStatusClick("On Time", "BROKERAGE", "AIR")}
                  className={`bg-green-50 border-2 rounded-xl p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    kpiStatusFilter === "On Time" && kpiTypeFilter === "BROKERAGE" && kpiTransportFilter === "AIR"
                      ? "border-green-500 ring-2 ring-green-200"
                      : "border-green-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-grey">On Time (1-2 days)</span>
                    <span className="text-xl font-bold text-green-700">
                      {analytics?.brokerageAirKPI.onTime || 0}
                    </span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-1.5">
                    <div
                      className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          analytics?.brokerageAirKPI.total > 0
                            ? (analytics.brokerageAirKPI.onTime / analytics.brokerageAirKPI.total) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-green-700 mt-1">
                    {analytics?.brokerageAirKPI.total > 0
                      ? ((analytics.brokerageAirKPI.onTime / analytics.brokerageAirKPI.total) * 100).toFixed(1)
                      : 0}% of total
                  </p>
                </div>

                {/* Delayed - AIR */}
                <div
                  onClick={() => handleKpiStatusClick("Delayed", "BROKERAGE", "AIR")}
                  className={`bg-red-50 border-2 rounded-xl p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    kpiStatusFilter === "Delayed" && kpiTypeFilter === "BROKERAGE" && kpiTransportFilter === "AIR"
                      ? "border-red-500 ring-2 ring-red-200"
                      : "border-red-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-grey">Delayed (&gt; 2 days)</span>
                    <span className="text-xl font-bold text-red-700">
                      {analytics?.brokerageAirKPI.delayed || 0}
                    </span>
                  </div>
                  <div className="w-full bg-red-200 rounded-full h-1.5">
                    <div
                      className="bg-red-500 h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          analytics?.brokerageAirKPI.total > 0
                            ? (analytics.brokerageAirKPI.delayed / analytics.brokerageAirKPI.total) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    {analytics?.brokerageAirKPI.total > 0
                      ? ((analytics.brokerageAirKPI.delayed / analytics.brokerageAirKPI.total) * 100).toFixed(1)
                      : 0}% of total
                  </p>
                </div>

                {/* No Arrival Data - AIR */}
                <div
                  onClick={() => handleKpiStatusClick("No Arrival Data", "BROKERAGE", "AIR")}
                  className={`bg-gray-50 border-2 rounded-xl p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    kpiStatusFilter === "No Arrival Data" && kpiTypeFilter === "BROKERAGE" && kpiTransportFilter === "AIR"
                      ? "border-gray-500 ring-2 ring-gray-200"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-grey">No Arrival Data</span>
                    <span className="text-xl font-bold text-gray-700">
                      {analytics?.brokerageAirKPI.noArrivalData || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-gray-500 h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          analytics?.brokerageAirKPI.total > 0
                            ? (analytics.brokerageAirKPI.noArrivalData / analytics.brokerageAirKPI.total) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-700 mt-1">
                    {analytics?.brokerageAirKPI.total > 0
                      ? ((analytics.brokerageAirKPI.noArrivalData / analytics.brokerageAirKPI.total) * 100).toFixed(1)
                      : 0}% of total
                  </p>
                </div>
              </div>
            </div>

            {/* SEA Transport Mode Section */}
            <div className="border-2 border-cyan-100 rounded-2xl p-4 bg-cyan-50/30">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl">🚢</span>
                <div>
                  <h4 className="text-lg font-bold text-blue">Sea Transport Mode</h4>
                  <p className="text-xs text-grey">Total: {analytics?.brokerageSeaKPI.total || 0} shipments</p>
                </div>
              </div>

              <div className="space-y-3">
                {/* On Time - SEA */}
                <div
                  onClick={() => handleKpiStatusClick("On Time", "BROKERAGE", "SEA")}
                  className={`bg-green-50 border-2 rounded-xl p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    kpiStatusFilter === "On Time" && kpiTypeFilter === "BROKERAGE" && kpiTransportFilter === "SEA"
                      ? "border-green-500 ring-2 ring-green-200"
                      : "border-green-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-grey">On Time (1-5 days)</span>
                    <span className="text-xl font-bold text-green-700">
                      {analytics?.brokerageSeaKPI.onTime || 0}
                    </span>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-1.5">
                    <div
                      className="bg-green-500 h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          analytics?.brokerageSeaKPI.total > 0
                            ? (analytics.brokerageSeaKPI.onTime / analytics.brokerageSeaKPI.total) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-green-700 mt-1">
                    {analytics?.brokerageSeaKPI.total > 0
                      ? ((analytics.brokerageSeaKPI.onTime / analytics.brokerageSeaKPI.total) * 100).toFixed(1)
                      : 0}% of total
                  </p>
                </div>

                {/* Slightly Delayed - SEA */}
                <div
                  onClick={() => handleKpiStatusClick("Slightly Delayed", "BROKERAGE", "SEA")}
                  className={`bg-orange-50 border-2 rounded-xl p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    kpiStatusFilter === "Slightly Delayed" && kpiTypeFilter === "BROKERAGE" && kpiTransportFilter === "SEA"
                      ? "border-orange-500 ring-2 ring-orange-200"
                      : "border-orange-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-grey">Slightly Delayed (6-10 days)</span>
                    <span className="text-xl font-bold text-orange-700">
                      {analytics?.brokerageSeaKPI.slightlyDelayed || 0}
                    </span>
                  </div>
                  <div className="w-full bg-orange-200 rounded-full h-1.5">
                    <div
                      className="bg-orange-500 h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          analytics?.brokerageSeaKPI.total > 0
                            ? (analytics.brokerageSeaKPI.slightlyDelayed / analytics.brokerageSeaKPI.total) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-orange-700 mt-1">
                    {analytics?.brokerageSeaKPI.total > 0
                      ? ((analytics.brokerageSeaKPI.slightlyDelayed / analytics.brokerageSeaKPI.total) * 100).toFixed(1)
                      : 0}% of total
                  </p>
                </div>

                {/* Delayed - SEA */}
                <div
                  onClick={() => handleKpiStatusClick("Delayed", "BROKERAGE", "SEA")}
                  className={`bg-red-50 border-2 rounded-xl p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    kpiStatusFilter === "Delayed" && kpiTypeFilter === "BROKERAGE" && kpiTransportFilter === "SEA"
                      ? "border-red-500 ring-2 ring-red-200"
                      : "border-red-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-grey">Delayed (&gt; 10 days)</span>
                    <span className="text-xl font-bold text-red-700">
                      {analytics?.brokerageSeaKPI.delayed || 0}
                    </span>
                  </div>
                  <div className="w-full bg-red-200 rounded-full h-1.5">
                    <div
                      className="bg-red-500 h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          analytics?.brokerageSeaKPI.total > 0
                            ? (analytics.brokerageSeaKPI.delayed / analytics.brokerageSeaKPI.total) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    {analytics?.brokerageSeaKPI.total > 0
                      ? ((analytics.brokerageSeaKPI.delayed / analytics.brokerageSeaKPI.total) * 100).toFixed(1)
                      : 0}% of total
                  </p>
                </div>

                {/* No Arrival Data - SEA */}
                <div
                  onClick={() => handleKpiStatusClick("No Arrival Data", "BROKERAGE", "SEA")}
                  className={`bg-gray-50 border-2 rounded-xl p-3 cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${
                    kpiStatusFilter === "No Arrival Data" && kpiTypeFilter === "BROKERAGE" && kpiTransportFilter === "SEA"
                      ? "border-gray-500 ring-2 ring-gray-200"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-grey">No Arrival Data</span>
                    <span className="text-xl font-bold text-gray-700">
                      {analytics?.brokerageSeaKPI.noArrivalData || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-gray-500 h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${
                          analytics?.brokerageSeaKPI.total > 0
                            ? (analytics.brokerageSeaKPI.noArrivalData / analytics.brokerageSeaKPI.total) * 100
                            : 0
                        }%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-700 mt-1">
                    {analytics?.brokerageSeaKPI.total > 0
                      ? ((analytics.brokerageSeaKPI.noArrivalData / analytics.brokerageSeaKPI.total) * 100).toFixed(1)
                      : 0}% of total
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 1: Monthly Comparison */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue/10 rounded-xl flex items-center justify-center">
                <i className="fas fa-chart-bar text-blue"></i>
              </div>
              <h3 className="text-2xl font-bold text-blue">
                {trendPeriod === "yearly"
                  ? "Yearly Trend"
                  : `${trendPeriod}-Month Trend`}
              </h3>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setTrendPeriod(3)}
                className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                  trendPeriod === 3
                    ? "bg-blue text-white shadow-lg"
                    : "bg-gray-100 text-grey hover:bg-gray-200"
                }`}
              >
                3M
              </button>
              <button
                onClick={() => setTrendPeriod(6)}
                className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                  trendPeriod === 6
                    ? "bg-blue text-white shadow-lg"
                    : "bg-gray-100 text-grey hover:bg-gray-200"
                }`}
              >
                6M
              </button>
              <button
                onClick={() => setTrendPeriod(12)}
                className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                  trendPeriod === 12
                    ? "bg-blue text-white shadow-lg"
                    : "bg-gray-100 text-grey hover:bg-gray-200"
                }`}
              >
                12M
              </button>
              <button
                onClick={() => setTrendPeriod("yearly")}
                className={`px-4 py-2 rounded-xl font-semibold transition-all duration-300 ${
                  trendPeriod === "yearly"
                    ? "bg-blue text-white shadow-lg"
                    : "bg-gray-100 text-grey hover:bg-gray-200"
                }`}
              >
                Yearly
              </button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analytics?.monthlyComparison || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                stroke={COLORS.grey}
                style={{ fontSize: "12px", fontWeight: "600" }}
              />
              <YAxis
                stroke={COLORS.grey}
                style={{ fontSize: "12px", fontWeight: "600" }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: "14px", fontWeight: "600" }}
                iconType="circle"
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke={COLORS.blue}
                strokeWidth={3}
                dot={{ fill: COLORS.blue, r: 6 }}
                activeDot={{ r: 8 }}
                name="Shipments"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Type Breakdown Table */}
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <i className="fas fa-table text-purple-600"></i>
            </div>
            <h3 className="text-2xl font-bold text-blue">Type Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold rounded-tl-2xl">
                    Shipment Type
                  </th>
                  <th className="px-6 py-4 text-center font-semibold">Count</th>
                  <th className="px-6 py-4 text-center font-semibold rounded-tr-2xl">
                    Percentage
                  </th>
                </tr>
              </thead>
              <tbody>
                {(analytics?.modeData || []).map((mode, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-blue/5 transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-grey">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {getShipmentTypeIcon(mode.name)}
                        </span>
                        <span>{mode.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center font-bold text-blue">
                      {mode.value}
                    </td>
                    <td className="px-6 py-4 text-center font-semibold text-grey">
                      {mode.percentage}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed Shipment Table with KPI Status */}
        <div ref={kpiTableRef} className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue/10 rounded-xl flex items-center justify-center">
                <i className="fas fa-list-alt text-blue"></i>
              </div>
              <h3 className="text-2xl font-bold text-blue">
                Shipment Details with KPI Status
              </h3>
            </div>
            {kpiStatusFilter && (
              <button
                onClick={() => {
                  setKpiStatusFilter(null);
                  setKpiTypeFilter(null);
                  setKpiTransportFilter(null);
                }}
                className="px-4 py-2 bg-grey/10 hover:bg-grey/20 text-grey font-medium rounded-xl transition-all duration-300 flex items-center gap-2"
              >
                <i className="fas fa-times"></i>
                Clear Filter: {kpiTypeFilter}{kpiTransportFilter ? ` (${kpiTransportFilter})` : ""} - {kpiStatusFilter}
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold rounded-tl-2xl">
                    Job Order
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">Type</th>
                  {kpiTypeFilter === "BROKERAGE" && (
                    <th className="px-4 py-3 text-left font-semibold">
                      Transport Mode
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-semibold">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    Arrival Date
                  </th>
                  <th className="px-4 py-3 text-left font-semibold">
                    Completion Date
                  </th>
                  <th className="px-4 py-3 text-center font-semibold">Days</th>
                  <th className="px-4 py-3 text-center font-semibold rounded-tr-2xl">
                    KPI Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {shipments
                  .filter((s) => {
                    const joDate = extractDateFromJobOrder(s.job_order);
                    if (!joDate) return false;

                    // Use Job Order date
                    const matchesMonth =
                      selectedMonth === "All"
                        ? true
                        : joDate.month === parseInt(selectedMonth);

                    const matchesYear =
                      selectedYear === "All"
                        ? true
                        : joDate.year === parseInt(selectedYear);

                    // Only show AIR, SEA, and BROKERAGE shipments for KPI
                    const isKpiEligible =
                      s.shipment_type === "AIR" || s.shipment_type === "SEA" || s.shipment_type === "BROKERAGE";

                    // Apply type filter only if it's AIR, SEA, or BROKERAGE (and if a specific type is selected)
                    const matchesType =
                      selectedType === "ALL"
                        ? isKpiEligible
                        : s.shipment_type === selectedType && isKpiEligible;

                    // Apply KPI status filter if set
                    let matchesKpiStatus = true;
                    if (kpiStatusFilter) {
                      const kpiStatus = getKPIStatus(s);
                      if (kpiStatusFilter === "No Arrival Data") {
                        matchesKpiStatus = !kpiStatus; // No KPI status means no arrival data
                      } else {
                        matchesKpiStatus = kpiStatus?.status === kpiStatusFilter;
                      }
                    }

                    // Apply KPI type filter if set (AIR, SEA, or BROKERAGE)
                    let matchesKpiType = true;
                    if (kpiTypeFilter) {
                      matchesKpiType = s.shipment_type === kpiTypeFilter;
                    }

                    // Apply transport mode filter if set (for BROKERAGE shipments)
                    let matchesTransportMode = true;
                    if (kpiTransportFilter && s.shipment_type === "BROKERAGE") {
                      matchesTransportMode = s.shipment_transport === kpiTransportFilter;
                    }

                    return matchesMonth && matchesYear && matchesType && matchesKpiStatus && matchesKpiType && matchesTransportMode;
                  })
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .slice(kpiTablePage * 10, (kpiTablePage + 1) * 10) // Pagination: 10 per page
                  .map((shipment, index) => {
                    const kpiStatus = getKPIStatus(shipment);
                    const daysDiff = kpiStatus?.days ?? "-";

                    // Get arrival date from remarks (only use aad field)
                    const arrivalRemark = shipment.remarks?.find(
                      (r) => r.status === "Arrival at Destination"
                    );
                    const arrivalDate = arrivalRemark?.aad;

                    // Get completion date from remarks
                    const completionRemark = shipment.remarks?.find(
                      (r) => r.status === "Delivered" || r.status === "Turnover"
                    );
                    const completionDate = completionRemark?.date;

                    return (
                      <tr
                        key={index}
                        onClick={() => openModal(shipment)}
                        className={`border-b border-gray-100 transition-all duration-300 cursor-pointer ${
                          kpiStatus
                            ? `${kpiStatus.bgColor} hover:opacity-80`
                            : "hover:bg-blue/5"
                        }`}
                      >
                        <td className="px-4 py-3 font-medium text-grey">
                          {shipment.job_order || "N/A"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xl">
                              {getShipmentTypeIcon(shipment.shipment_type)}
                            </span>
                            <span className="font-semibold text-grey">
                              {shipment.shipment_type}
                            </span>
                          </div>
                        </td>
                        {kpiTypeFilter === "BROKERAGE" && (
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">
                                {shipment.shipment_transport === "AIR" ? "✈️" : shipment.shipment_transport === "SEA" ? "🚢" : "-"}
                              </span>
                              <span className="font-semibold text-grey">
                                {shipment.shipment_transport || "-"}
                              </span>
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <span className="text-orange-600 font-semibold">
                            {shipment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-grey">
                          {arrivalDate
                            ? dayjs(arrivalDate).format("MMM DD, YYYY")
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-grey">
                          {completionDate
                            ? dayjs(completionDate).format("MMM DD, YYYY")
                            : "-"}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="font-bold text-blue">
                            {daysDiff}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {kpiStatus ? (
                            <span
                              className={`px-3 py-1 ${kpiStatus.bgColor} ${kpiStatus.textColor} border-2 ${kpiStatus.borderColor} rounded-full text-xs font-bold`}
                            >
                              {kpiStatus.status}
                            </span>
                          ) : (
                            <span className="text-grey text-xs">
                              No Arrival Date
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
            {(() => {
              const filteredKpiShipments = shipments.filter((s) => {
                const joDate = extractDateFromJobOrder(s.job_order);
                if (!joDate) return false;

                // Use Job Order date
                const matchesMonth =
                  selectedMonth === "All"
                    ? true
                    : joDate.month === parseInt(selectedMonth);

                const matchesYear =
                  selectedYear === "All"
                    ? true
                    : joDate.year === parseInt(selectedYear);

                // Only show AIR, SEA, and BROKERAGE shipments for KPI
                const isKpiEligible =
                  s.shipment_type === "AIR" || s.shipment_type === "SEA" || s.shipment_type === "BROKERAGE";

                // Apply type filter only if it's AIR, SEA, or BROKERAGE
                const matchesType =
                  selectedType === "ALL"
                    ? isKpiEligible
                    : s.shipment_type === selectedType && isKpiEligible;

                // Apply KPI status filter if set
                let matchesKpiStatus = true;
                if (kpiStatusFilter) {
                  const kpiStatus = getKPIStatus(s);
                  if (kpiStatusFilter === "No Arrival Data") {
                    matchesKpiStatus = !kpiStatus;
                  } else {
                    matchesKpiStatus = kpiStatus?.status === kpiStatusFilter;
                  }
                }

                // Apply KPI type filter if set (AIR, SEA, or BROKERAGE)
                let matchesKpiType = true;
                if (kpiTypeFilter) {
                  matchesKpiType = s.shipment_type === kpiTypeFilter;
                }

                // Apply transport mode filter if set (for BROKERAGE shipments)
                let matchesTransportMode = true;
                if (kpiTransportFilter && s.shipment_type === "BROKERAGE") {
                  matchesTransportMode = s.shipment_transport === kpiTransportFilter;
                }

                return matchesMonth && matchesYear && matchesType && matchesKpiStatus && matchesKpiType && matchesTransportMode;
              });

              const totalFilteredShipments = filteredKpiShipments.length;
              const pageCount = Math.ceil(totalFilteredShipments / 10);

              if (totalFilteredShipments === 0) {
                return (
                  <div className="text-center py-8">
                    <p className="text-grey text-lg">
                      {kpiStatusFilter
                        ? `No ${kpiTypeFilter} ${kpiStatusFilter} shipments found for the selected filters`
                        : "No Air or Sea shipments found for the selected filters"}
                    </p>
                  </div>
                );
              }

              return (
                <div className="mt-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-gray-50 rounded-b-2xl">
                    <p className="text-sm text-grey">
                      Showing {kpiTablePage * 10 + 1} to{" "}
                      {Math.min(
                        (kpiTablePage + 1) * 10,
                        totalFilteredShipments
                      )}{" "}
                      of {totalFilteredShipments} shipments
                    </p>
                    {pageCount > 1 && (
                      <ReactPaginate
                        previousLabel={"← Previous"}
                        nextLabel={"Next →"}
                        pageCount={pageCount}
                        onPageChange={(data) => setKpiTablePage(data.selected)}
                        forcePage={kpiTablePage}
                        containerClassName={"flex items-center gap-2"}
                        pageClassName={"hidden sm:block"}
                        pageLinkClassName={
                          "px-3 py-1 rounded-lg border border-gray-300 hover:bg-blue hover:text-white transition-colors"
                        }
                        previousClassName={"block"}
                        previousLinkClassName={
                          "px-3 py-1 rounded-lg border border-gray-300 hover:bg-blue hover:text-white transition-colors"
                        }
                        nextClassName={"block"}
                        nextLinkClassName={
                          "px-3 py-1 rounded-lg border border-gray-300 hover:bg-blue hover:text-white transition-colors"
                        }
                        activeClassName={"bg-blue text-white"}
                        activeLinkClassName={"bg-blue text-white border-blue"}
                        disabledClassName={"opacity-50 cursor-not-allowed"}
                      />
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* View Shipment Modal */}
        {selectedShipment && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-xl w-full max-w-2xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-300">
              {/* Modal Header */}
              <div className="bg-blue px-8 py-6 relative">
                <button
                  onClick={closeModal}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center text-white hover:text-white transition-all duration-200"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-2xl">
                    <i className="fas fa-eye text-white text-2xl"></i>
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-2xl">
                      Shipment Details
                    </h2>
                    <p className="text-white/80">
                      View comprehensive shipment information
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-8 max-h-96 overflow-y-auto">
                <div className="space-y-6">
                  {/* Main Info Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex flex-col p-4 bg-blue/5 rounded-2xl">
                        <span className="text-grey font-medium mb-2">
                          Tracking Number
                        </span>
                        <span className="text-blue font-bold text-lg font-mono break-all">
                          {selectedShipment.tracking_no}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-gray-50 rounded-2xl">
                        <span className="text-grey font-medium">
                          Job Order
                        </span>
                        <span className="text-blue font-bold">
                          {selectedShipment.job_order || "N/A"}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-blue/5 rounded-2xl">
                        <span className="text-grey font-medium">
                          Created By
                        </span>
                        <span className="text-blue font-bold">
                          {selectedShipment.created_by}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-gray-50 rounded-2xl">
                        <span className="text-grey font-medium">
                          Client Name
                        </span>
                        <span className="text-blue font-bold">
                          {selectedShipment.shipper_name}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-blue/5 rounded-2xl">
                        <span className="text-grey font-medium mb-2">
                          Shipment Type
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold w-fit ${getShipmentTypeColor(
                            selectedShipment.shipment_type
                          )}`}
                        >
                          {selectedShipment.shipment_type}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex flex-col p-4 bg-gray-50 rounded-2xl">
                        <span className="text-grey font-medium">
                          Expected Delivery
                        </span>
                        <span className="text-blue font-bold">
                          {selectedShipment.expected_delivery_date || "Not set"}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-blue/5 rounded-2xl">
                        <span className="text-grey font-medium">Origin</span>
                        <span className="text-blue font-bold">
                          {selectedShipment.shipper_city}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-gray-50 rounded-2xl">
                        <span className="text-grey font-medium">
                          Destination
                        </span>
                        <span className="text-blue font-bold">
                          {selectedShipment.receiver_city}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-blue/5 rounded-2xl">
                        <span className="text-grey font-medium mb-2">
                          Status
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold w-fit ${getStatusColor(
                            selectedShipment.status
                          )}`}
                        >
                          {selectedShipment.status}
                        </span>
                      </div>

                      <div className="flex flex-col p-4 bg-gray-50 rounded-2xl">
                        <span className="text-grey font-medium mb-2">
                          KPI Status
                        </span>
                        {(() => {
                          const kpiStatus = getKPIStatus(selectedShipment);
                          return kpiStatus ? (
                            <div className="space-y-2">
                              <span
                                className={`px-3 py-1 rounded-full text-sm font-semibold w-fit ${kpiStatus.bgColor} ${kpiStatus.textColor} border-2 ${kpiStatus.borderColor}`}
                              >
                                {kpiStatus.status}
                              </span>
                              <p className="text-sm text-grey">
                                {kpiStatus.days} days from arrival to{" "}
                                {selectedShipment.status === "Delivered" ||
                                selectedShipment.status === "Turnover"
                                  ? "completion"
                                  : "current"}
                              </p>
                            </div>
                          ) : (
                            <span className="text-grey text-sm">
                              No KPI data available
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-8 py-6 flex flex-col sm:flex-row gap-4 justify-between">
                <button
                  onClick={closeModal}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-grey font-medium rounded-2xl transition-all duration-200 hover:shadow-md"
                >
                  <i className="fas fa-arrow-left mr-2"></i>
                  Go Back
                </button>
                <button
                  onClick={downloadShipmentPDF}
                  className="px-6 py-3 bg-blue hover:bg-blue/90 text-white font-medium rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                >
                  <i className="fas fa-download mr-2"></i>
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Analytics;
